
<template>
    <div class="d-none" id="billingFormInitializer"></div>
</template>

<script setup>
import { mapGetters, mapMutations } from "@/store/helper/vuex";
import { onMounted, computed, markRaw, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { mergeDynamicForm } from "@/components/helper/util"
import { EventBus as oldEventBus } from "@/scripts/event-bus.js";

import AddressSuggestions from "@/components/checkout/injectComponents/AddressSuggestions.vue";
import DaumFindInject from "@/components/checkout/injectComponents/DaumFindInject.vue";
import DaumCloseInject from "@/components/checkout/injectComponents/DaumCloseInject.vue";
import SaveAddressInject from "@/components/checkout/injectComponents/SaveAddressInject.vue";

const { t } = useI18n();

const { checkout, formFields, lastFormData, vueVariables, billingDynamicStructure, billingVars, billingLocale, currentStage} = mapGetters();
const { setBillingDynamicStructure, setBillingLocale } = mapMutations();

const pdict = checkout || mapGetters().pdict;

const pdictExists = computed(() => { return Object.values(pdict.value).length === 0 ? false : true })

const isCustomerAuthenticated = computed(() => { return vueVariables && vueVariables.value.hasOwnProperty('isCustomerAuthenticated') ? vueVariables.value.isCustomerAuthenticated  : true });
const billingDataAddressMode = computed(() => { return billingVars && billingVars.value.dataAddressMode ? billingVars.value.dataAddressMode : null });
const selectedBillingAddressUUID = computed(() => { return billingVars && billingVars.value.selectedAddressUUID ? billingVars.value.selectedAddressUUID : null });
const selectedBillingAddress = computed(() => { return  selectedBillingAddressUUID.value ? pdict.value.allAddresses.find((address) => { return address.UUID === selectedBillingAddressUUID.value; }) : null });

const getVisibleOnActiveStageInject = computed(() => { return currentStage && currentStage.value })

const isEditModalClosed = () => {
  let editModal = document.getElementById('edit-or-add-address-modal');
  return editModal ? !editModal.classList.contains('show') : false;
}

const initBillingForm = () => {
    const billingCountryCode = computed(() => { return formFields.value['billing'].country || pdict.value.billingCountryCode });
    const isNonDefaultEUCountry = computed(() => { return pdict.value.euCountryCodeList.includes(billingCountryCode.value) && billingCountryCode.value !== 'FR' });

    const billingLocale = computed(() => {
        let dynamicFormBillingStructureJson = pdict.value.dynamicForm.dynamicFormBillingStructureJson;
        const dynamicFormBillingStructureJsonKeys = Object.keys(dynamicFormBillingStructureJson);
        let computedLocale;

        if (pdict.value.locale.includes(billingCountryCode.value)) {
            computedLocale = billingCountryCode.value === 'US' ?  'default' : pdict.value.locale;
        } else {
            let countryCodeKey = dynamicFormBillingStructureJsonKeys.find(key => key.includes(billingCountryCode.value));
            if (isNonDefaultEUCountry.value && !countryCodeKey) {
                countryCodeKey = dynamicFormBillingStructureJsonKeys.find(key => key.includes('en_FR'));
            }
            computedLocale = countryCodeKey ? countryCodeKey : 'default';
        }

        return computedLocale;
    });

    setBillingLocale(billingLocale);

    let dynamicFormStructure = computed(() => { return billingDynamicStructure.value[billingLocale.value] ? billingDynamicStructure.value[billingLocale.value] : pdict.value.dynamicForm.dynamicFormBillingStructureJson[billingLocale.value] });

    const formDataFields = computed(() => lastFormData.value['billing']);

    const prepareBillingForm = () => {

        const billingModel = computed(() => { return pdict.value.order ? pdict.value.order.billing : {} });

        const modelBillingAddress = computed(() => { return (billingModel.value.billingAddress && billingModel.value.billingAddress.address) ? billingModel.value.billingAddress.address : {} });

        const { FRESH_addressLineCharLimit } = window.vue.customPreferences

        const selectedCountryCode = computed(() => {
            return formDataFields.value.country.options.find((country) => {
                return formFields.value['billing'].country ? formFields.value['billing'].country === country.id : (modelBillingAddress.value.countryCode && modelBillingAddress.value.countryCode.value || pdict.value.billingCountryCode) === country.id
            });
        });

        const stateCodeFilteredOptions = computed(() => {
            return selectedCountryCode.value ? formDataFields.value.stateCode.options.filter((state) => {
                return state.id.slice(-2) === selectedCountryCode.value.htmlValue;
            }) : formDataFields.value.stateCode.options;
        });

        const stateCodeValue = computed(() => {
            return formFields.value['billing'].stateCode || modelBillingAddress.value.stateCode;
        });

        const selectedStateCode = computed(() => {
            return stateCodeFilteredOptions.value.find((state) => {
                return stateCodeValue.value ? (state.value === stateCodeValue.value && state.id.includes(selectedCountryCode.value.htmlValue)) : null;
            });
        });

        const HKDistrictFilteredOptions = computed(() => {
            return formDataFields.value.HKCity.options.filter((district) => {
                return district.id.includes(selectedStateCode.value && selectedStateCode.value.htmlValue || 'HK');
            });
        });

        const areaCodeFilteredOptions = computed(() => {
            return formDataFields.value.areaCode.options.filter((areaCode) => {
                return pdict.value.billingAreaCodeMap[selectedCountryCode.value.htmlValue] ? pdict.value.billingAreaCodeMap[selectedCountryCode.value.htmlValue].includes(areaCode.htmlValue) : pdict.value.billingAreaCodeMap['default'].includes(areaCode.htmlValue);
            }) || formDataFields.value.areaCode.options;
        });

        const selectedAreaCode = computed(() => {
            return areaCodeFilteredOptions.value.find((areaCode, index) => {
                return selectedBillingAddress.value ? selectedBillingAddress.value[billingCountryCode.value === 'HK' ? 'postalCode' : 'areaCode'] === areaCode.htmlValue : index === 0;
            });
        });

        let finalAdditionalProperties = {};

        if (selectedCountryCode.value.id === 'HK') {
            finalAdditionalProperties = {
                postalCode: {
                    defaultValue: computed(() => selectedAreaCode.value ? selectedAreaCode.value.htmlValue : ''),
                    filteredOptions: computed(() => areaCodeFilteredOptions.value),
                    tag: "select",
                    columnLayoutClasses: "col-sm-3",
                    autocomplete: "billing postal-code"
                },
                stateCode: {
                    label: t('label.input.region'),
                }
            }
        } else if (selectedCountryCode.value.id === 'SG') {

            finalAdditionalProperties = {
                city: {
                    tag: "input",
                    type: "hidden",
                    value: "Singapore",
                    defaultValue: "Singapore"
                },
                stateCode: {
                    tag: "input",
                    type: "hidden",
                    value: "SG",
                    defaultValue: "SG",
                }
            }
        }

        let defaultAdditionalProperties = {
            addressId: {
                visible: isCustomerAuthenticated,
                formId: 'addressId',
                defaultValue: computed(() => pdict.value.billingAddressID || ''),
                events: {
                    input: true
                },
            },
            firstName: {
                events: {
                    input: true
                },
                defaultValue: computed(() => modelBillingAddress.value.firstName || ''),
                formId: "billingFirstName"
            },
            lastName: {
                events: {
                    input: true
                },
                defaultValue: computed(() => modelBillingAddress.value.lastName || ''),
                name: "lastName",
                formId: "billingLastName"
            },
            country: {
                events: {
                    input: true,
                },
                defaultValue: computed(() => selectedCountryCode.value ? selectedCountryCode.value.htmlValue : ''),
                tag: "select",
                formGroupClasses: "billingCountry",
                formId: "billingCountry",
                autocomplete: "billing country"
            },
            address1: {
                visible: true,
                events: {
                    input: true,
                },
                defaultValue: computed(() => modelBillingAddress.value.address1 || ''),
                attributes: {
                    maxLength: FRESH_addressLineCharLimit || 30,
                },
                formId: "billingAddressOne",
                classes: "daum-readonly",
                autocomplete: "billing address-line1"
            },
            address2: {
                events: {
                    input: true,
                },
                defaultValue: computed(() => modelBillingAddress.value.address2 || ''),
                formId: "billingAddressTwo",
                autocomplete: "billing address-line2"
            },
            postalCode: {
                classes: 'daum-readonly',
                events: {
                    input: true,
                },
                defaultValue: computed(() => modelBillingAddress.value.postalCode || ''),
                type: "tel",
                tag: "input",
                formId: "billingZipCode",
                columnLayoutClasses: "col-sm-6",
                columnCustomClasses: "zipcode-field",
                labelCustomClasses: "",
                autocomplete: "billing postal-code",
            },
            stateCode: {
                filteredOptions: computed(() => stateCodeFilteredOptions.value),
                events: {
                    input: true,
                    change: 'updateHKBillingDistrictsOptions'
                },
                defaultValue: computed(() => selectedStateCode.value ? selectedStateCode.value.htmlValue : (stateCodeFilteredOptions.value[0] ? stateCodeFilteredOptions.value[0].htmlValue : '')),
                label: t('label.input.state'),
                tag: "select",
                formId: "billingState",
                columnLayoutClasses: "col-sm-6",
                formGroupClasses: "js-hk-regions billingState billing-select-container",
                autocomplete: "billing address-level1"
            },
            HKCity: {
                filteredOptions: computed(() => HKDistrictFilteredOptions.value),
                events: {
                    input: true,
                },
                defaultValue: computed(() => modelBillingAddress.value.city || (HKDistrictFilteredOptions.value && HKDistrictFilteredOptions.value.length ? HKDistrictFilteredOptions.value[0].htmlValue : '')),
                tag: "select",
                formId: "billingAddressHKCity",
                columnLayoutClasses: "col-sm-6",
                formGroupClasses: "js-hk-districts billingAddressHKCity billing-select-container",
                autocomplete: "billing address-level2"
            },
            city: {
                events: {
                    input: true,
                },
                defaultValue: computed(() => modelBillingAddress.value.city || ''),
                formId: "billingAddressCity",
                autocomplete: "billing address-level2"
            },
            areaCode: {
                events: {
                    input: true,
                },
                defaultValue: computed(() => selectedAreaCode.value ? selectedAreaCode.value.htmlValue : ''),
                filteredOptions: computed(() => areaCodeFilteredOptions.value),
                tag: "select",
                formId: "billingAreaCode",
                columnLayoutClasses: "col-sm-3",
                formGroupClasses: "checkout-select field-areacode",
            },
            phone: {
                events: {
                    input: true,
                    blur: 'phoneValidationPhoneBlur'
                },
                defaultValue: computed(() => modelBillingAddress.value.phone || ''),
                formId: "phone",
                columnLayoutClasses: "col-sm-9",
                classes: "phone",
                type: "tel",
                autocomplete: "tel",
                attributes: {
                    maxLength: 20,
                    minLength: 0
                }
            },
            "address-suggestion-inject": {
                classInstance: markRaw(AddressSuggestions),
                type: 'inject',
            },
            "daum-find-inject": {
                classInstance: markRaw(DaumFindInject),
            },
            "daum-close-inject": {
                classInstance: markRaw(DaumCloseInject),
            },
            "save-address-inject": {
            classInstance: markRaw(SaveAddressInject),
            visible: getVisibleOnActiveStageInject,
            type: 'inject',
            },
        }

        Object.keys(finalAdditionalProperties).length ? mergeDynamicForm(finalAdditionalProperties, defaultAdditionalProperties, { mergeModeSupport: true }) : (finalAdditionalProperties = defaultAdditionalProperties);

        mergeDynamicForm(finalAdditionalProperties, formDataFields.value, { mergeModeSupport: true });

        let dynamicFormStructureKeysList = []

        for (const rowKey of Object.keys(dynamicFormStructure.value)) {
            for (let field of dynamicFormStructure.value[rowKey].fields) {
                if (finalAdditionalProperties[field.name]) {
                    mergeDynamicForm(field, finalAdditionalProperties[field.name], { mergeModeSupport: true });
                }
                field.type !== 'inject' && dynamicFormStructureKeysList.push(field.name);
            }
        }

        dynamicFormStructure.value.get = function (key) {
            return Object.values(this).flatMap(row => row.fields).find(field => field ? field.name === key : null);
        };

        dynamicFormStructure.value.keys = dynamicFormStructureKeysList;

        setBillingDynamicStructure({ locale: billingLocale.value, value: dynamicFormStructure.value });
    }

    prepareBillingForm();

    const refreshBillingForm = watch([billingLocale, billingCountryCode, billingDataAddressMode, selectedBillingAddressUUID], ([newBillingLocale, newBillingCountryCode, newBillingDataAddressMode, newSelectedBillingAddressUUID], [oldBillingLocale, oldBillingCountryCode, oldBillingAddressMode, oldSelectedBillingAddressUUID]) => {

        if (newBillingLocale !== oldBillingLocale) {
            if (!billingDynamicStructure.value[newBillingLocale]) {
                prepareBillingForm();
            } else {
                setBillingDynamicStructure({ locale: newBillingLocale, value: dynamicFormStructure.value });
            }
        }

        if (newSelectedBillingAddressUUID !== oldSelectedBillingAddressUUID || isEditModalClosed()) {
                return;
        } else {
            if (newBillingCountryCode !== oldBillingCountryCode) {

                const getSelectFields = (locale) => {
                    const selectFieldKeys = [];

                    for (const key of billingDynamicStructure.value[locale].keys) {
                        const field = billingDynamicStructure.value[locale].get(key);

                        if (field.tag === 'select' && field.name !== 'country') {
                            selectFieldKeys.push(key);
                        }
                    }
                    return selectFieldKeys;
                }

                let newStructureSelectKeys = getSelectFields(newBillingLocale);
                let oldStructureSelectKeys = getSelectFields(oldBillingLocale);
                let differentStructureKeys = billingDynamicStructure.value[newBillingLocale].keys
                    .filter(element => !billingDynamicStructure.value[oldBillingLocale].keys.includes(element))
                    .concat(billingDynamicStructure.value[oldBillingLocale].keys.filter(element => !billingDynamicStructure.value[newBillingLocale].keys.includes(element)));

                const specificFormFields = Array.from(new Set([...newStructureSelectKeys, ...oldStructureSelectKeys, ...differentStructureKeys]))

                oldEventBus.$emit('clearSpecificFormFields', {
                    formName: 'billing',
                    currentDynamicStructure: billingDynamicStructure.value[newBillingLocale],
                    specificFormFields
                })
            }
        }

    }, {
        flush: 'post'
    });


}

oldEventBus.$on('updateHKBillingDistrictsOptions', () => {
    if (billingDynamicStructure.value && billingDynamicStructure.value[billingLocale.value]) {
        oldEventBus.$emit('clearSpecificFormFields', {
            formName: 'billing',
            currentDynamicStructure: billingDynamicStructure.value[billingLocale.value],
            specificFormFields: ['HKCity']
        })
    }
    return;
});

onMounted(() => {
    if (pdictExists.value) {
        initBillingForm();
    } else {
        const initBillingFormWatcher = watch(pdictExists, (newVal) => {
            if (newVal === true) {
                initBillingForm();
                initBillingFormWatcher();
            }
        })
    }
})

</script>